import React from 'react';
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Select,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useTiktokCreatorInfo } from '../../hook/useTiktokCreatorInfo';
import type { TiktokPrivacyLevel } from '../../types/tiktokDirectPost.types';
import {
    getTiktokCommercialLabelPrompt,
    getTiktokPublishDeclaration,
    isTiktokPrivacyDisabledForBranded,
    resolveTiktokPublishConsentType,
    TIKTOK_PRIVACY_LABELS,
} from '../../utils/tiktokDirectPostUx';
import type { TiktokPostFields } from './platformPostFields';

export interface TikTokComposeTheme {
    muted: string;
    white: string;
    gold: string;
    red: string;
    mint?: string;
    surface?: string;
    border?: string;
}

const DEFAULT_THEME: TikTokComposeTheme = {
    muted: '#7a7a7a',
    white: '#f5f5f5',
    gold: '#ffc965',
    red: '#ff7351',
    mint: '#00fdc1',
    surface: '#201f1f',
    border: 'rgba(255,255,255,0.08)',
};

const selectSx = (theme: TikTokComposeTheme) => ({
    bgcolor: theme.surface,
    color: theme.white,
    borderRadius: 1,
    fontSize: 14,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 201, 101, 0.4)' },
    '& .MuiSvgIcon-root': { color: theme.muted },
});

export interface TikTokComposePanelProps {
    fields: TiktokPostFields;
    onChange: (patch: Partial<TiktokPostFields>) => void;
    /** Fetch creator info when true (TikTok card enabled + connected) */
    active: boolean;
    mediaDurationSec?: number;
    mediaPreviewUrl?: string;
    creatorNicknameFallback?: string;
    theme?: TikTokComposeTheme;
}

export function TikTokComposePanel({
    fields,
    onChange,
    active,
    mediaDurationSec,
    mediaPreviewUrl,
    creatorNicknameFallback,
    theme = DEFAULT_THEME,
}: TikTokComposePanelProps) {
    const { creatorInfo, loading, error } = useTiktokCreatorInfo({
        enabled: active,
        mediaDurationSec,
    });

    const commercial = fields.tiktok_commercial_content;
    const consentType = resolveTiktokPublishConsentType(commercial);
    const declaration = getTiktokPublishDeclaration(consentType);
    const commercialPrompt = getTiktokCommercialLabelPrompt(commercial);
    const privacyOptions = creatorInfo?.privacy_level_options?.length
        ? creatorInfo.privacy_level_options
        : (Object.keys(TIKTOK_PRIVACY_LABELS) as TiktokPrivacyLevel[]);

    const displayName = creatorInfo?.creator_nickname || creatorNicknameFallback || creatorInfo?.creator_username;
    const displayHandle = creatorInfo?.creator_username;

    const patchCommercial = (patch: Partial<TiktokPostFields['tiktok_commercial_content']>) => {
        onChange({
            tiktok_commercial_content: { ...commercial, ...patch },
        });
    };

    const handleBrandedContentChange = (checked: boolean) => {
        patchCommercial({ branded_content: checked });
        if (checked && fields.tiktok_privacy_level === 'SELF_ONLY') {
            onChange({ tiktok_privacy_level: 'PUBLIC_TO_EVERYONE' });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                    <CircularProgress size={16} sx={{ color: theme.gold }} />
                    <Typography sx={{ fontSize: 12, color: theme.muted }}>Loading TikTok account…</Typography>
                </Box>
            ) : null}

            {error ? (
                <Alert severity="error" sx={{ fontSize: 12, py: 0.5 }}>
                    {error}
                </Alert>
            ) : null}

            {displayName ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        border: `1px solid ${theme.border}`,
                    }}>
                    {creatorInfo?.creator_avatar_url ? (
                        <Box
                            component="img"
                            src={creatorInfo.creator_avatar_url}
                            alt=""
                            sx={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
                        />
                    ) : null}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: theme.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                            }}>
                            Posting to TikTok as
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: theme.white, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {displayName}
                        </Typography>
                        {displayHandle ? (
                            <Typography sx={{ fontSize: 11, color: theme.gold }}>@{displayHandle}</Typography>
                        ) : null}
                    </Box>
                </Box>
            ) : null}

            {creatorInfo && !creatorInfo.can_post_now ? (
                <Alert severity="warning" sx={{ fontSize: 12 }}>
                    {creatorInfo.blocked_reason_message ??
                        'You cannot post to TikTok right now. Please try again later.'}
                </Alert>
            ) : null}

            {creatorInfo && creatorInfo.duration_allowed === false ? (
                <Alert severity="error" sx={{ fontSize: 12 }}>
                    Video exceeds TikTok&apos;s maximum length ({creatorInfo.max_video_post_duration_sec}s).
                </Alert>
            ) : null}

            {creatorInfo && !creatorInfo.api_client_audited ? (
                <Alert severity="info" sx={{ fontSize: 12 }}>
                    Posts may be private until our TikTok app completes audit. Only &quot;Only me&quot; visibility may
                    be available.
                </Alert>
            ) : null}

            {mediaPreviewUrl ? (
                <Box>
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: theme.muted,
                            mb: 0.75,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}>
                        Preview
                    </Typography>
                    <Box
                        component="video"
                        src={mediaPreviewUrl}
                        controls
                        playsInline
                        sx={{
                            width: '100%',
                            maxHeight: 220,
                            borderRadius: 2,
                            bgcolor: '#000',
                            border: `1px solid ${theme.border}`,
                        }}
                    />
                </Box>
            ) : null}

            <Typography sx={{ fontSize: 11, color: theme.muted, lineHeight: 1.45 }}>
                Caption comes from the shared field above. Review all TikTok settings before publishing.
            </Typography>

            <Box>
                <Typography sx={{ fontSize: 10, color: theme.muted, mb: 0.5 }}>Who can view *</Typography>
                <Select
                    size="small"
                    displayEmpty
                    value={fields.tiktok_privacy_level}
                    onChange={e =>
                        onChange({ tiktok_privacy_level: e.target.value as TiktokPostFields['tiktok_privacy_level'] })
                    }
                    fullWidth
                    sx={selectSx(theme)}
                    renderValue={selected =>
                        selected ? (
                            TIKTOK_PRIVACY_LABELS[selected as TiktokPrivacyLevel]
                        ) : (
                            <Box component="span" sx={{ color: theme.muted }}>
                                Select visibility
                            </Box>
                        )
                    }>
                    {privacyOptions.map(option => {
                        const disabled = isTiktokPrivacyDisabledForBranded(option, commercial);
                        const item = (
                            <MenuItem key={option} value={option} disabled={disabled}>
                                {TIKTOK_PRIVACY_LABELS[option]}
                            </MenuItem>
                        );
                        return disabled ? (
                            <Tooltip
                                key={option}
                                title="Branded content visibility cannot be set to private."
                                placement="left">
                                <span>{item}</span>
                            </Tooltip>
                        ) : (
                            item
                        );
                    })}
                </Select>
            </Box>

            <Box>
                <Typography sx={{ fontSize: 10, color: theme.muted, mb: 0.5 }}>Interactions</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(
                        [
                            {
                                key: 'tiktok_allow_comment' as const,
                                label: 'Allow comments',
                                disabled: creatorInfo?.interactions?.comment_disabled,
                            },
                            {
                                key: 'tiktok_allow_duet' as const,
                                label: 'Allow duet',
                                disabled: creatorInfo?.interactions?.duet_disabled,
                            },
                            {
                                key: 'tiktok_allow_stitch' as const,
                                label: 'Allow stitch',
                                disabled: creatorInfo?.interactions?.stitch_disabled,
                            },
                        ] as const
                    ).map(opt => (
                        <Tooltip key={opt.key} title={opt.disabled ? 'Disabled in your TikTok app settings' : ''}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={fields[opt.key]}
                                        disabled={Boolean(opt.disabled)}
                                        onChange={e => onChange({ [opt.key]: e.target.checked })}
                                        sx={{
                                            color: theme.muted,
                                            '&.Mui-checked': { color: theme.gold },
                                            '&.Mui-disabled': { opacity: 0.45 },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: opt.disabled ? theme.muted : theme.white,
                                        }}>
                                        {opt.label}
                                    </Typography>
                                }
                            />
                        </Tooltip>
                    ))}
                </Box>
            </Box>

            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.border}`,
                    bgcolor: 'rgba(255,255,255,0.02)',
                }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: 12, color: theme.white, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Commercial content disclosure
                    </Typography>
                    <Switch
                        size="small"
                        checked={commercial.disclosure_enabled}
                        onChange={e => {
                            const enabled = e.target.checked;
                            patchCommercial({
                                disclosure_enabled: enabled,
                                ...(!enabled ? { your_brand: false, branded_content: false } : {}),
                            });
                        }}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: theme.gold },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#feb700' },
                        }}
                    />
                </Box>
                {commercial.disclosure_enabled ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={commercial.your_brand}
                                    onChange={e => patchCommercial({ your_brand: e.target.checked })}
                                    sx={{ color: theme.muted, '&.Mui-checked': { color: theme.gold } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 12, color: theme.white }}>Your brand</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={commercial.branded_content}
                                    onChange={e => handleBrandedContentChange(e.target.checked)}
                                    sx={{ color: theme.muted, '&.Mui-checked': { color: theme.gold } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 12, color: theme.white }}>Branded content</Typography>}
                        />
                        {!commercial.your_brand && !commercial.branded_content ? (
                            <Typography sx={{ fontSize: 11, color: theme.red, mt: 0.5 }}>
                                Select at least one option to publish commercial content.
                            </Typography>
                        ) : null}
                        {commercialPrompt ? (
                            <Typography sx={{ fontSize: 11, color: theme.gold, mt: 0.5 }}>
                                {commercialPrompt}
                            </Typography>
                        ) : null}
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 11, color: theme.muted }}>
                        Turn on if this video promotes you, a brand, product, or service.
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid rgba(255, 201, 101, 0.15)`,
                    bgcolor: 'rgba(255, 201, 101, 0.04)',
                }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={fields.tiktok_publish_consent_accepted}
                            onChange={e => onChange({ tiktok_publish_consent_accepted: e.target.checked })}
                            sx={{ color: theme.muted, '&.Mui-checked': { color: theme.gold } }}
                        />
                    }
                    label={
                        <Typography sx={{ fontSize: 12, color: theme.white, lineHeight: 1.45 }}>
                            {declaration}
                        </Typography>
                    }
                />
            </Box>

            <Typography sx={{ fontSize: 11, color: theme.muted, lineHeight: 1.45 }}>
                After publishing, it may take a few minutes for your video to appear on TikTok.
            </Typography>
        </Box>
    );
}
